<template>
  <div class="us-widget">
    <div class="us-header">
      <span class="us-header-title">
        <i class="fa fa-hand-fist" aria-hidden="true"></i>
        UFC Center
      </span>
      <router-link class="us-header-link" to="/ufc">Open</router-link>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="us-skeleton"></div>

    <!-- Error -->
    <div v-else-if="error" class="us-error">
      Couldn't load UFC data.
      <button class="us-retry" @click="load">Retry</button>
    </div>

    <template v-else-if="loaded">
      <!-- Next event -->
      <router-link
        v-if="nextEvent"
        class="us-next"
        :to="`/ufc/events/${nextEvent.eventId}`"
      >
        <div class="us-next-top">
          <span class="us-next-short">{{ nextEvent.shortName || 'Next Event' }}</span>
          <span v-if="countdown" class="us-countdown">{{ countdown }}</span>
        </div>
        <span class="us-next-name">{{ nextEvent.name }}</span>
        <span v-if="nextEvent.mainEventLabel" class="us-next-main">{{ nextEvent.mainEventLabel }}</span>
        <span v-if="dateLine" class="us-next-date">{{ dateLine }}</span>
      </router-link>

      <div v-else class="us-empty">No upcoming event scheduled.</div>

      <!-- Recent results -->
      <div v-if="recentEvents.length" class="us-recent">
        <span class="us-recent-label">Recent results</span>
        <router-link
          v-for="ev in recentEvents"
          :key="ev.eventId"
          class="us-recent-row"
          :to="`/ufc/events/${ev.eventId}`"
        >
          <span class="us-recent-name">{{ ev.shortName || ev.name }}</span>
          <span v-if="ev.mainEventLabel" class="us-recent-main">{{ ev.mainEventLabel }}</span>
          <i class="fa fa-chevron-right" aria-hidden="true"></i>
        </router-link>
      </div>

      <router-link class="us-footer-cta" to="/ufc">
        Events, rankings, fighter analytics &amp; history
        <i class="fa fa-arrow-right" aria-hidden="true"></i>
      </router-link>
    </template>
  </div>
</template>

<script>
import MmaSportsService from '@/services/MmaSportsService';
import { countdownLabel, fmtLocalDateFull, fmtLocalTime } from '@/utils/ufcFormat';

const COUNTDOWN_TICK_MS = 60 * 1000;

/**
 * Compact UFC product entry point rendered on the /sports/mma news feed.
 * Replaces the legacy ESPN-shaped scoreboard without touching the news list.
 */
export default {
  name: 'UfcScores',
  data() {
    return {
      loading: false,
      loaded: false,
      error: false,
      nextEvent: null,
      recentEvents: [],
      countdown: null,
      countdownInterval: null,
    };
  },
  computed: {
    dateLine() {
      if (!this.nextEvent || !this.nextEvent.startTimeUtc) return null;
      return `${fmtLocalDateFull(this.nextEvent.startTimeUtc)} · ${fmtLocalTime(this.nextEvent.startTimeUtc)}`;
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    this.stopCountdown();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = false;
      try {
        const response = await MmaSportsService.getOverview();
        const data = response.data || {};
        this.nextEvent = data.nextEvent || null;
        this.recentEvents = (Array.isArray(data.recentEvents) ? data.recentEvents : []).slice(0, 3);
        this.loaded = true;
        this.startCountdown();
      } catch {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    tickCountdown() {
      this.countdown = this.nextEvent ? countdownLabel(this.nextEvent.startTimeUtc) : null;
    },
    startCountdown() {
      this.stopCountdown();
      this.tickCountdown();
      this.countdownInterval = setInterval(this.tickCountdown, COUNTDOWN_TICK_MS);
    },
    stopCountdown() {
      if (!this.countdownInterval) return;
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    },
  },
};
</script>

<style scoped>
.us-widget {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-family: 'Titillium Web', sans-serif;
}
.us-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.us-header-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.9px;
  color: #475569;
}
.us-header-title i { color: #b45309; }
.us-header-link {
  font-size: 11.5px;
  font-weight: 800;
  color: var(--ufc-gold-text, #b45309);
  text-decoration: none;
}
.us-header-link:hover { text-decoration: underline; }

.us-skeleton {
  height: 110px;
  background: linear-gradient(90deg, #e2e8f0 0%, #f1f5f9 50%, #e2e8f0 100%);
  background-size: 200% 100%;
  border-radius: 10px;
  animation: us-shimmer 1.5s infinite;
}
@keyframes us-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.us-error {
  font-size: 12px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 8px;
}
.us-retry {
  border: none;
  background: none;
  color: #b45309;
  font-weight: 800;
  font-size: 12px;
  cursor: pointer;
  font-family: inherit;
  text-decoration: underline;
}
.us-empty {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
}

.us-next {
  display: flex;
  flex-direction: column;
  gap: 3px;
  background: linear-gradient(150deg, #0f172a 0%, #1e293b 62%, #292524 100%);
  border-radius: 12px;
  padding: 12px 14px;
  text-decoration: none;
  transition: opacity 0.15s;
}
.us-next:hover { opacity: 0.93; }
.us-next:focus-visible { outline: 2px solid #f59e0b; outline-offset: 2px; }
.us-next-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.us-next-short {
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--ufc-gold-on-dark, #fbbf24);
}
.us-countdown {
  font-size: 11px;
  font-weight: 900;
  color: var(--ufc-gold-on-dark, #fbbf24);
  font-variant-numeric: tabular-nums;
}
.us-next-name {
  font-size: 15px;
  font-weight: 900;
  color: #f8fafc;
  line-height: 1.25;
}
.us-next-main {
  font-size: 12px;
  font-weight: 700;
  color: #cbd5e1;
}
.us-next-date {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
}

.us-recent {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.us-recent-label {
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: #64748b;
  margin-bottom: 2px;
}
.us-recent-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  text-decoration: none;
  transition: border-color 0.15s;
}
.us-recent-row:hover { border-color: #fbbf24; }
.us-recent-row:focus-visible { outline: 2px solid #f59e0b; outline-offset: 2px; }
.us-recent-name {
  flex-shrink: 0;
  font-size: 11.5px;
  font-weight: 800;
  color: #111827;
}
.us-recent-main {
  font-size: 11px;
  color: #64748b;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.us-recent-row i {
  margin-left: auto;
  font-size: 9px;
  color: #cbd5e1;
}

.us-footer-cta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 11.5px;
  font-weight: 700;
  color: #475569;
  text-decoration: none;
  border-top: 1px dashed #e2e8f0;
  padding-top: 9px;
  transition: color 0.15s;
}
.us-footer-cta:hover { color: #b45309; }
.us-footer-cta i { font-size: 10px; color: #b45309; }
</style>
